import { uniq } from 'lodash';
import { useEffect } from 'react';
import { usePrevious } from 'react-use';

import { useGPSurgeriesOptions } from '../../../hooks/referrals/useGPSurgeriesOptions';
import { UserProfileResponse } from '../../../types/authentication';
import { SiteData } from '../../../types/sites';
import { validateMembersSitesSpecificData } from './AddHospitalStepUpdateMembersUtils';
import { useMergedSiteSettings } from '../../../query/sites';
import { isGpSurgeryRequired } from '../../../utils/siteUtils';

export const useAddHospitalStepUpdateMembersData = ({
  user,
  newSite,
  currentTrustIds,
  currentSiteIds,
  onLoadingFinished,
  onMembersUpdateNotNeeded,
}: {
  user: UserProfileResponse | undefined;
  // site to be added to the user
  newSite: SiteData | undefined;
  // list of user assigned trusts ids without new site trust id
  currentTrustIds: string[] | undefined;
  // list of user assigned sites ids without new site id
  currentSiteIds: string[] | undefined;
  onLoadingFinished: () => void;
  // Fired after members are loaded and their data are complete
  onMembersUpdateNotNeeded: () => void;
}) => {
  const trustIds =
    currentTrustIds && newSite
      ? uniq([...currentTrustIds, newSite.trust_id])
      : undefined;
  const siteIds =
    currentSiteIds && newSite
      ? uniq([...currentSiteIds, newSite.id])
      : undefined;
  const { data: siteSettings, isLoading: isSiteSettingsLoading } =
    useMergedSiteSettings(siteIds);

  const { gpSurgeriesOptions, isLoading: isGpOptionsLoading } =
    useGPSurgeriesOptions({
      gpFilters: {
        trustIDs: trustIds,
      },
      options: {
        enabled: Boolean(
          trustIds && siteSettings && isGpSurgeryRequired(siteSettings),
        ),
      },
    });

  const isLoading =
    !user ||
    !newSite ||
    !currentTrustIds ||
    !currentSiteIds ||
    isSiteSettingsLoading ||
    !siteSettings ||
    isGpOptionsLoading;
  const prevIsLoading = usePrevious(isLoading);
  useEffect(() => {
    if (prevIsLoading !== false && !isLoading) {
      if (
        validateMembersSitesSpecificData(
          [user, ...(user.other_members || [])],
          siteSettings,
        )
      ) {
        onMembersUpdateNotNeeded();
      } else {
        onLoadingFinished();
      }
    }
  }, [
    prevIsLoading,
    isLoading,
    onLoadingFinished,
    onMembersUpdateNotNeeded,
    user,
    siteSettings,
  ]);

  return isLoading
    ? {
        isLoading,
      }
    : {
        isLoading,
        siteSettings,
        gpSurgeriesOptions,
        members: [user, ...(user.other_members || [])],
        newSite,
      };
};
